<script>
import RouteStopStatusLabel from "../../components/RouteStopStatus.vue";
import EditStopDialog from "@/views/trips/edit/dialogs/EditStopDialog.vue";
import FacilityInfo from "@/views/trips/components/FacilityInfo.vue";
import { OrderFreightState, OrderStopType } from "@/data/order";
import AlertMessage from "@/components/AlertMessage.vue";
import { RouteStatus } from "@/data/trip.js";
import { useOrder, useTrip } from "@/composables/index.js";
import RouteStopIcon from "@/components/routeStop/RouteStopIcon.vue";
import RouteStopTypePicker from "@/views/trips/edit/blocks/RouteStopTypePicker.vue";

const { getFreightName, STOP_PICK_UP, STOP_RELOAD, STOP_DELIVERY, timeTypesMap, getStopTime, createStopName } =
  useOrder();
const { isTravelOrderCancelled } = useTrip();

export default {
  name: "RouteStopBlock",
  components: {
    RouteStopTypePicker,
    RouteStopIcon,
    AlertMessage,
    FacilityInfo,
    EditStopDialog,
    RouteStopStatusLabel,
  },
  props: {
    stop: {
      type: Object,
      required: true,
    },
    route: {
      type: Object,
    },
    allowEditUsage: {
      type: Boolean,
      default: false,
    },
    tripStops: {
      type: Array,
      required: true,
    },
    orderFreights: {
      type: Array,
      required: true,
    },
    routes: {
      type: Array,
      required: true,
    },
    tripManager: {
      type: Object,
      required: true,
    },
    tripFromApi: {
      type: [Object, null],
      required: true,
    },
  },
  emits: ["delete", "changed:route"],
  data() {
    return {
      editStopDialog: false,
      selectedStopType: OrderStopType.notSelected,
    };
  },
  computed: {
    OrderStopType() {
      return OrderStopType;
    },
    OrderFreightState() {
      return OrderFreightState;
    },
    STOP_PICK_UP() {
      return STOP_PICK_UP;
    },
    STOP_RELOAD() {
      return STOP_RELOAD;
    },
    STOP_DELIVERY() {
      return STOP_DELIVERY;
    },
    timeTypesMap() {
      return timeTypesMap;
    },
    isStopCancelled() {
      const relatedTravelOrders = this.route.travelOrders.filter((to) => {
        return to?.tempId && this.stop.travelOrderTempIds?.includes(to.tempId);
      });

      return Boolean(relatedTravelOrders.length && relatedTravelOrders.every((to) => isTravelOrderCancelled(to)));
    },
    stopClass() {
      if (this.isStopCancelled) {
        return "bg-red-lighten-5";
      } else if (this.isUpdated) {
        return "border-warning";
      } else if (this.isDeleted) {
        return "bg-grey-lighten-4";
      } else if (this.isPlanned) {
        return "bg-indigo-lighten-5";
      } else {
        return "bg-grey-lighten-4";
      }
    },
    routeStopFreights() {
      let number = 1;
      return this.stop.freights
        .map((f) => this.route.freights.find((rf) => rf.id === f))
        .filter((f) => f !== undefined && f.id)
        .map((f) => {
          const orderFreight = this.orderFreights.find((of) => of.id === f.freightId);
          return {
            ...f,
            number: number,
            name: getFreightName(f, number++, "", false, true),
            orderFreight,
          };
        })
        .sort((a, b) => parseInt(a.number) - parseInt(b.number));
    },
    routeStopFreightsFromAPI() {
      const routesFreights = this.tripFromApi?.routes.flatMap((route) => route.freights);

      if (!routesFreights?.length) {
        return [];
      }

      return this.stop.freights
        .map((f) => routesFreights.find((rf) => rf.id === f))
        .filter((f) => f !== undefined && f.id);
    },
    unusedRouteFreights() {
      return this.tripManager.getUnusedFreights(this.route);
    },
    deletedRouteStopFreights() {
      return this.routeStopFreights.filter((f) => f.orderFreight?.isDeleted);
    },
    isPickup() {
      return this.stop.type === OrderStopType.pickup;
    },
    isDeleted() {
      return this.tripStop.isDeleted || !!(this.stop.isDeleted && this.stop?.id);
    },
    isUpdated() {
      return this.deletedRouteStopFreights.length > 0;
    },
    isPlanned() {
      return this.route.status === RouteStatus.planned;
    },
    isReload() {
      return this.stop.isReload;
    },
    isDelivery() {
      return this.stop.type === OrderStopType.delivery;
    },
    tripStop() {
      return this.tripStops.find((os) => os.id === this.stop?.tripStopId);
    },
    showAddStop() {
      return !this.stop.use;
    },
    name() {
      return createStopName(this.stop.type, this.stop.number);
    },
  },
  methods: {
    onStopChanged(stop, freights) {
      this.$emit("changed:route", stop, freights);
    },
    getStopTimeFunction(stop) {
      return getStopTime(stop);
    },
    checkIsSelected(routeFreight) {
      return !this.unusedRouteFreights.some((item) => item.id === routeFreight.id);
    },
    getFreightClass(freight) {
      if (freight.isDeleted) {
        return "freight--deleted";
      }

      if (!this.checkIsSelected(freight) && this.isPickup) {
        return "freight--not-selected";
      }

      if (freight.state === OrderFreightState.updated) {
        return "freight--updated";
      }

      if (freight.state === OrderFreightState.new) {
        return "freight--new";
      }
    },
    openEditStopDialog(type) {
      this.selectedStopType = type;
      this.editStopDialog = true;
    },
  },
};
</script>

<template>
  <div :class="{ 'reload-stop': stop.isReload && stop.type !== OrderStopType.notSelected }" class="trip-stops__block">
    <route-stop-type-picker v-if="stop.type === OrderStopType.notSelected" :stop="stop" @chose="openEditStopDialog" />

    <div v-else-if="showAddStop" class="trip-stops__unused pa-4 rounded">
      <div class="trip-stops__block-header">
        <div class="d-flex align-center justify-space-between mb-2">
          <div class="d-flex align-center flex-wrap">
            <route-stop-icon
              :is-deleted="stop.isDeleted"
              :is-reload="isReload"
              :type="stop.type"
              class="mr-2"
              size="20"
            ></route-stop-icon>
            <span
              :class="isDeleted ? 'text-grey' : 'text-grey-darken-1'"
              class="font-weight-600 mr-3 font-size-12"
              data-qa="stop-type"
            >
              {{ name }}
            </span>

            <router-link
              v-if="stop.order"
              :to="{ name: 'order-view', params: { id: stop.order.orderId || stop.order.id } }"
              class="font-size-12 font-weight-medium text-primary text-decoration-none"
              data-qa="route-stop-order-number"
              target="_blank"
            >
              Order #{{ stop.order.number }}
            </router-link>
          </div>
        </div>
      </div>

      <div class="d-flex justify-center align-center trip-stops__unused__inner">
        <v-btn
          :disabled="isStopCancelled"
          class="text-primary text-uppercase"
          variant="flat"
          @click="openEditStopDialog(stop.type)"
        >
          <v-icon class="mr-1" icon="mdi-plus"></v-icon>
          Add
          <template v-if="isPickup">pick up</template>
          <template v-else>delivery</template>
        </v-btn>
      </div>
    </div>

    <div v-else :class="stopClass" class="trip-stops__block-inner pa-4 rounded">
      <div class="mb-6 trip-stops__block-header">
        <div class="d-flex align-center justify-space-between mb-2">
          <div class="d-flex align-center flex-wrap">
            <route-stop-icon
              :is-deleted="stop.isDeleted"
              :is-reload="isReload"
              :type="stop.type"
              class="mr-2"
              size="20"
            ></route-stop-icon>
            <span
              :class="isDeleted ? 'text-grey' : 'text-grey-darken-1'"
              class="font-weight-600 mr-3 font-size-12"
              data-qa="stop-type"
            >
              {{ stop.name }}
            </span>
            <router-link
              v-if="stop.order && !isDeleted"
              :to="{ name: 'order-view', params: { id: stop.order.orderId || stop.order.id } }"
              class="font-size-12 font-weight-medium text-primary text-decoration-none"
              data-qa="route-stop-order-number"
              target="_blank"
            >
              Order #{{ stop.order.number }}
            </router-link>
            <span v-if="isDeleted" class="text-grey font-size-12 font-weight-medium">
              Order #{{ stop.order.number }}
            </span>
          </div>
          <div>
            <v-btn
              v-if="stop.type === STOP_RELOAD"
              class="text-red text--accent-2 bg-transparent"
              icon="mdi-delete-outline"
              size="x-small"
              variant="flat"
              @click="$emit('delete')"
            />
            <v-btn
              v-if="!isStopCancelled"
              :disabled="tripStop.isDeleted"
              color="primary"
              density="comfortable"
              icon="mdi-pencil-outline"
              size="x-small"
              variant="text"
              @click="openEditStopDialog(stop.type)"
            />
          </div>
        </div>
        <div>
          <route-stop-status-label :stop="stop" is-badge></route-stop-status-label>
        </div>
      </div>

      <div class="mb-6 trip-stops__block-body">
        <facility-info
          v-if="stop.facility && !isReload"
          :disabled="isDeleted"
          :facility="stop.facility"
          :updated="!!stop.oldFacilityId && !isStopCancelled"
          class="mb-5"
        ></facility-info>
        <div v-if="stop.timeType" :class="isDeleted ? 'text-grey' : 'text-grey-darken-1'" class="d-flex align-center">
          <v-icon class="mr-2" size="20">mdi-clock-outline</v-icon>
          <span class="font-weight-medium" data-qa="stop-time">
            {{ timeTypesMap[stop.timeType] }}: {{ getStopTimeFunction(stop) }}
          </span>
        </div>
      </div>
      <div class="trip-stops__block-footer">
        <div class="d-flex">
          <div class="text-grey-darken-1 mr-2">Freight:</div>
          <div
            v-if="!routeStopFreights.length"
            class="text-grey-darken-1 font-weight-500"
            data-qa="not-added-freight-label"
          >
            Not added
          </div>

          <div
            v-else
            :class="{
              'text-error': isStopCancelled,
              'text-grey': isDeleted,
              'text-grey-darken-2': !isStopCancelled && !isDeleted,
            }"
            class="font-weight-medium w-100"
            data-qa="freight-size"
          >
            <div
              v-for="freight in routeStopFreights"
              :key="freight.id"
              :class="getFreightClass(freight)"
              class="freight"
            >
              <span class="freight__name">
                {{ freight.name }}
              </span>

              <span v-if="!checkIsSelected(freight) && isPickup" class="text-orange-darken-3 font-weight-500"
                >Not selected</span
              >
            </div>
          </div>
        </div>

        <alert-message v-if="!routeStopFreights.length && isDelivery && unusedRouteFreights.length" class="mt-2">
          Select Route
          <span v-for="(item, index) in unusedRouteFreights" :key="item.id">
            freight#{{ item.number }}<span v-if="index !== unusedRouteFreights.length - 1">,</span>
          </span>
          at least at one DEL
        </alert-message>

        <template v-if="!isReload">
          <alert-message
            v-for="deletedFreight in deletedRouteStopFreights"
            :key="deletedFreight.id"
            bg-color="indigo-lighten-5"
            border-color="none"
            class="mt-3"
            color="indigo"
            icon="mdi-information-outline"
            icon-color="indigo"
          >
            Freight #{{ deletedFreight.number }} will be removed after re-dispatch confirmation
          </alert-message>

          <alert-message v-if="isPickup && routeStopFreights.length === 0" class="mt-3">
            Route stop must have at least one route freight
          </alert-message>

          <alert-message
            v-if="isDeleted"
            bg-color="indigo-lighten-5"
            border-color="none"
            class="mt-3"
            color="indigo"
            icon="mdi-information-outline"
            icon-color="indigo"
          >
            Stop will be removed from trip after re-dispatch confirmation
          </alert-message>
        </template>
      </div>
    </div>

    <edit-stop-dialog
      v-if="editStopDialog"
      v-model="editStopDialog"
      :allow-edit-usage="allowEditUsage"
      :initial-route-stop-freights="routeStopFreights"
      :order-freights="orderFreights"
      :route="route"
      :route-freights="route.freights"
      :route-stop-freights-from-api="routeStopFreightsFromAPI"
      :routes="routes"
      :stop="stop"
      :stop-type="selectedStopType"
      :trip-stops="tripStops"
      @changed="onStopChanged"
    />
  </div>
</template>

<style lang="scss" scoped>
@import "@/assets/style/color";

.reload-stop {
  margin-bottom: 8px;

  .trip-stops__unused {
    border: none;
  }
}

.trip-stops__unused {
  border: 1px solid $grey-light;
  height: 100%;

  .trip-stops__unused__inner {
    height: calc(100% - 20px);
  }
}

.trip-stops__block-inner {
  height: 100%;
}

.badge {
  margin-top: -16px;
  margin-right: -16px;
}

.freight {
  display: flex;
  justify-content: space-between;
  align-items: center;
  width: 100%;
}

.freight__name {
  position: relative;
  width: max-content;

  &:after {
    position: absolute;
    content: "";
    display: none;
    top: 50%;
    transform: translateY(-50%);
    width: 6px;
    height: 6px;
    border-radius: 50%;
    text-decoration: none !important;
    right: -14px;
  }
}

.freight--deleted {
  text-decoration-line: line-through;
  text-decoration-color: $grey-lighter-contrast;
  color: $grey-lighter-contrast !important;

  .freight__name {
    &:after {
      display: block;
      background-color: $grey-lighter-contrast;
      color: $grey-lighter-contrast;
    }
  }
}

.freight--updated {
  .freight__name {
    &:after {
      display: block;
      background-color: orange;
    }
  }
}

.freight--new {
  .freight__name {
    &:after {
      display: block;
      background-color: green;
    }
  }
}

.freight--not-selected {
  color: $warning;
}
</style>
